// Registers a scene template's fonts as global @font-face rules so Konva can use
// them. The family name MUST be sanitizeFontFamily(PostScriptName) to match what
// sceneToKonva emits (Konva writes ctx.font unquoted -> a dotted family silently
// falls back to 10px sans-serif). the renderer awaits document.fonts.ready, so
// these must be registered before generating.

import { LATIN_FALLBACK_FAMILY, sanitizeFontFamily } from './sceneToKonva';

interface FontLike {
  urls?: string[] | null;
}

const STYLE_ID = 'gm-scene-fonts';

// The bundled English/Latin last-resort, served from public/fonts. Injected on
// EVERY registerSceneFonts call (even a scene with no template fonts) so the
// fontFamilyStack tail (… , FbPracticaPro-Medium , sans-serif) always resolves —
// matching the export Lambda, which registers the same file with node-canvas.
const LATIN_FALLBACK_FACE =
  `@font-face{font-family:'${LATIN_FALLBACK_FAMILY}';`
  + `src:url('${process.env.PUBLIC_URL || ''}/fonts/FbPracticaPro-Medium.otf') format('opentype');`
  + 'font-display:block;}';

export function registerSceneFonts(fonts: Record<string, FontLike | null | undefined> | undefined): void {
  if (typeof document === 'undefined') return;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  const templateCss = Object.entries(fonts || {})
    .filter(([, font]) => font && font.urls && font.urls.length)
    .map(([name, font]) => {
      const family = sanitizeFontFamily(name); // name = PostScript name from the importer
      const url = (font as FontLike).urls![0];
      const fmt = url.endsWith('.woff2') ? 'woff2' : url.endsWith('.woff') ? 'woff' : 'opentype';
      return `@font-face{font-family:'${family}';src:url('${url}') format('${fmt}');font-display:block;}`;
    })
    .join('\n');

  const css = templateCss ? `${LATIN_FALLBACK_FACE}\n${templateCss}` : LATIN_FALLBACK_FACE;
  if (style.textContent !== css) style.textContent = css;
}

/**
 * Trigger + await the ACTUAL download of each font used by the scene's text.
 * `document.fonts.ready` alone is NOT enough: an @font-face that hasn't been used
 * yet isn't "pending", so ready resolves immediately and Konva measures/draws with
 * fallback metrics (thin text) and never re-flows. `document.fonts.load(...)` forces
 * the download. Pass the specs from `textFontSpecs(nodes)`.
 */
export async function ensureSceneFontsLoaded(specs: Array<{ family: string; size: number }>): Promise<void> {
  const fonts = (typeof document !== 'undefined' && (document as any).fonts) || null;
  if (!fonts?.load) return;
  await Promise.all(
    specs.map(({ family, size }) =>
      fonts.load(`${Math.max(1, Math.ceil(size))}px '${family}'`).catch(() => undefined)),
  );
  if (fonts.ready) await fonts.ready;
}
